// src/components/PaymentModeSelector.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { getCreditOrganizations } from '../services/organizationService';

const PaymentModeSelector = ({ 
  value, 
  onChange, 
  error, 
  helperText, 
  disabled = false
}) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // Load organizations for credit billing
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoading(true);
        let orgs = await getCreditOrganizations();
        // Fallback: if API returns empty, try all organizations
        if (!Array.isArray(orgs) || orgs.length === 0) {
          try {
            const all = await (await import('../services/organizationService')).getAllOrganizations();
            orgs = Array.isArray(all) ? all : [];
          } catch (_) {
            // ignore secondary failure
          }
        }
        // Keep only active ones if flag exists
        const normalized = (orgs || []).filter((o) => o == null ? false : (o.isActive !== false));
        setOrganizations(normalized);
      } catch (error) {
        console.error('Error loading organizations:', error);
        setOrganizations([]);
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, []);


  const handlePaymentModeChange = (event) => {
    const mode = event.target.value;
    const newValue = {
      mode,
      organizationId: mode === 'credit' ? null : undefined,
      organizationName: mode === 'credit' ? null : undefined
    };
    onChange(newValue);
    setSelectedOrganization(null);
  };

  const handleOrganizationChange = (event) => {
    const organizationId = event.target.value;
    const organization = organizations.find(org => org.id === organizationId);
    
    const newValue = {
      ...value,
      organizationId,
      organizationName: organization?.name || null
    };
    onChange(newValue);
    setSelectedOrganization(organization);
  };

  const getPaymentModeLabel = (mode) => {
    switch (mode) {
      case 'cash':
        return 'Cash Payment';
      case 'credit':
        return 'Credit (Organization)';
      default:
        return 'Select Payment Mode';
    }
  };

  return (
    <Box>
      <FormControl fullWidth size="small" error={error} disabled={disabled}>
        <InputLabel>Payment Mode</InputLabel>
        <Select
          value={value?.mode || ''}
          onChange={handlePaymentModeChange}
          label="Payment Mode"
        >
          <MenuItem value="cash">
            Cash Payment
          </MenuItem>
          <MenuItem value="credit">
            Credit (Organization)
          </MenuItem>
        </Select>
      </FormControl>

      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}

      {/* Organization Selection for Credit */}
      {value?.mode === 'credit' && (
        <Box sx={{ mt: 1 }}>
          <FormControl fullWidth size="small" disabled={disabled || loading}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={value?.organizationId || ''}
              onChange={handleOrganizationChange}
              label="Organization"
            >
              {organizations.length === 0 ? (
                <MenuItem value="" disabled>
                  No credit-eligible organizations available
                </MenuItem>
              ) : (
                organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    <Typography variant="body1">{org.name}</Typography>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {organizations.length === 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              No organizations found. Add organizations and assign credit tariffs in admin.
            </Alert>
          )}
        </Box>
      )}

      {/* Tariff Information Display - Hidden as per requirement */}

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
};

export default PaymentModeSelector;
